<template>
  <vs-popup id="popupImprovementEffect" title="AI 위험도 개선효과 분석 결과" :active.sync="showPopup" :width="popupWidth">
    <div v-if="improvementData">
      <!-- PC 레이아웃 (768px 이상) -->
      <div class="desktop-layout" v-show="!isMobile">
        <div class="improvement-container">
          <!-- 헤더 정보 -->
          <div class="header-info mb-6">
            <div class="improvement-summary">
              <div class="summary-item">
                <div class="summary-label left">개선전 위험도</div>
                <div class="summary-value" :class="currentRiskClass">{{ currentRisk }}점</div>
              </div>
              <div class="summary-arrow">
                <feather-icon icon="ArrowRightIcon" size="28" />
              </div>
              <div class="summary-improvement">
                <div class="improvement-badge">{{ riskReduction }}점 감소</div>
              </div>
              <div class="summary-arrow">
                <feather-icon icon="ArrowRightIcon" size="28" />
              </div>
              <div class="summary-item">
                <div class="summary-value" :class="improvedRiskClass">{{ improvementData.개선후_위험성 || 0 }}점</div>
                <div class="summary-label right">개선후 위험도</div>
              </div>
            </div>
          </div>

          <!-- 상세 개선효과 -->
          <div class="improvement-details">
            <h4 class="section-title">
              <feather-icon icon="TrendingUpIcon" size="20" class="mr-2" />
              상세 개선효과
            </h4>

            <div class="detail-grid">
              <div class="detail-card">
                <div class="detail-header">
                  <feather-icon icon="AlertTriangleIcon" size="16" class="mr-2 text-orange-500" />
                  <span class="detail-title">가능성 변화</span>
                </div>
                <div class="detail-content">
                  {{ improvementData.가능성_변화 }}
                </div>
              </div>

              <div class="detail-card">
                <div class="detail-header">
                  <feather-icon icon="AlertOctagonIcon" size="16" class="mr-2 text-red-500" />
                  <span class="detail-title">중대성 변화</span>
                </div>
                <div class="detail-content">
                  {{ improvementData.중대성_변화 }}
                </div>
              </div>

              <div class="detail-card">
                <div class="detail-header">
                  <feather-icon icon="BarChartIcon" size="16" class="mr-2 text-blue-500" />
                  <span class="detail-title">위험도 변화</span>
                </div>
                <div class="detail-content">
                  {{ improvementData.위험성_변화 }}
                </div>
              </div>

              <div class="detail-card highlight">
                <div class="detail-header">
                  <feather-icon icon="StarIcon" size="16" class="mr-2 text-yellow-500" />
                  <span class="detail-title">주요 개선요인</span>
                </div>
                <div class="detail-content">
                  {{ improvementData.주요_개선요인 }}
                </div>
              </div>
            </div>
          </div>

          <!-- AI 분석 안내 -->
          <div class="ai-info mt-6">
            <div class="ai-badge">
              <feather-icon icon="ZapIcon" size="16" class="mr-2" />
              AI 분석 결과
            </div>
            <p class="ai-description">위 분석 결과는 AI가 제공된 위험감소대책을 바탕으로 예상되는 개선효과를 분석한 것입니다. 실제 적용 시에는 현장 상황을 고려하여 추가적인 검토가 필요할 수 있습니다.</p>
          </div>
        </div>

        <!-- PC 하단 버튼 -->
        <div class="button-container">
          <vs-button color="primary" @click="handleClose"> 확인 </vs-button>
        </div>
      </div>

      <!-- 모바일 레이아웃 (768px 미만) -->
      <div class="mobile-layout" v-show="isMobile">
        <!-- 모바일 헤더 -->
        <div class="mobile-header mobile-sticky-header">
          <div class="header-title">
            <feather-icon icon="TrendingUpIcon" size="18" class="mr-2" />
            <span>개선효과 분석 결과</span>
          </div>
        </div>

        <!-- 스크롤 가능한 컨텐츠 영역 -->
        <div class="mobile-content-wrapper">
          <!-- 모바일 개선 요약 -->
          <div class="mobile-improvement-summary">
            <div class="mobile-risk-comparison">
              <div class="mobile-risk-item before">
                <div class="risk-label">개선전</div>
                <div class="risk-value" :class="currentRiskClass">{{ currentRisk }}점</div>
              </div>
              <div class="mobile-arrow-down">
                <feather-icon icon="ArrowDownIcon" size="24" />
                <div class="improvement-badge-mobile">{{ riskReduction }}점 감소</div>
              </div>
              <div class="mobile-risk-item after">
                <div class="risk-label">개선후</div>
                <div class="risk-value" :class="improvedRiskClass">{{ improvementData.개선후_위험성 || 0 }}점</div>
              </div>
            </div>
          </div>

          <!-- 모바일 상세 정보 -->
          <div class="mobile-details-section">
            <h4 class="mobile-section-title">
              <feather-icon icon="ListIcon" size="16" class="mr-2" />
              상세 개선효과
            </h4>

            <div class="mobile-detail-cards">
              <div class="mobile-detail-card">
                <div class="mobile-detail-header">
                  <feather-icon icon="AlertTriangleIcon" size="14" class="mr-2 text-orange-500" />
                  <span class="mobile-detail-title">가능성 변화</span>
                </div>
                <div class="mobile-detail-content">
                  {{ improvementData.가능성_변화 }}
                </div>
              </div>

              <div class="mobile-detail-card">
                <div class="mobile-detail-header">
                  <feather-icon icon="AlertOctagonIcon" size="14" class="mr-2 text-red-500" />
                  <span class="mobile-detail-title">중대성 변화</span>
                </div>
                <div class="mobile-detail-content">
                  {{ improvementData.중대성_변화 }}
                </div>
              </div>

              <div class="mobile-detail-card">
                <div class="mobile-detail-header">
                  <feather-icon icon="BarChartIcon" size="14" class="mr-2 text-blue-500" />
                  <span class="mobile-detail-title">위험도 변화</span>
                </div>
                <div class="mobile-detail-content">
                  {{ improvementData.위험성_변화 }}
                </div>
              </div>

              <div class="mobile-detail-card highlight">
                <div class="mobile-detail-header">
                  <feather-icon icon="StarIcon" size="14" class="mr-2 text-yellow-500" />
                  <span class="mobile-detail-title">주요 개선요인</span>
                </div>
                <div class="mobile-detail-content">
                  {{ improvementData.주요_개선요인 }}
                </div>
              </div>
            </div>
          </div>

          <!-- 모바일 AI 안내 -->
          <div class="mobile-ai-info">
            <div class="mobile-ai-badge">
              <feather-icon icon="ZapIcon" size="14" class="mr-2" />
              AI 분석 결과
            </div>
            <p class="mobile-ai-description">위 분석 결과는 AI가 제공된 위험감소대책을 바탕으로 예상되는 개선효과를 분석한 것입니다. 실제 적용 시에는 현장 상황을 고려하여 추가적인 검토가 필요할 수 있습니다.</p>
          </div>

          <!-- 하단 여백 -->
          <div class="mobile-bottom-spacer"></div>
        </div>

        <!-- 모바일 하단 버튼 (sticky) -->
        <div class="mobile-button-container mobile-sticky-buttons">
          <vs-button color="primary" @click="handleClose" class="mobile-button mobile-button-primary"> 확인 </vs-button>
        </div>
      </div>
    </div>
  </vs-popup>
</template>

<script>
export default {
  name: "PopupImprovementEffect",
  props: {
    value: Boolean,
    improvementData: {
      type: Object,
      default: null,
    },
    currentRisk: {
      type: Number,
      default: 0,
    },
  },
  computed: {
    showPopup: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value);
      },
    },
    isMobile() {
      return this.windowWidth < 768;
    },
    popupWidth() {
      return this.isMobile ? "95vw" : "800px";
    },
    riskReduction() {
      if (this.improvementData && this.improvementData.개선후_위험성 && this.currentRisk) {
        const reduction = this.currentRisk - this.improvementData.개선후_위험성;
        return !isNaN(reduction) ? reduction : 0;
      }
      return 0;
    },
    riskLevelIcon() {
      const riskValue = (this.improvementData && this.improvementData.개선후_위험성) || 0;
      if (riskValue >= 16) return "FrownIcon"; // 매우높음
      if (riskValue >= 9) return "MehIcon"; // 높음/약간높음
      if (riskValue >= 4) return "MehIcon"; // 보통/낮음
      if (riskValue >= 1) return "SmileIcon"; // 매우낮음
      return "MehIcon";
    },
    riskLevelClass() {
      const riskValue = (this.improvementData && this.improvementData.개선후_위험성) || 0;
      if (riskValue >= 16) return "risk-very-high"; // 매우높음 - 빨간색
      if (riskValue >= 9) return "risk-high"; // 높음/약간높음 - 주황색
      if (riskValue >= 4) return "risk-medium"; // 보통/낮음 - 노란색
      if (riskValue >= 1) return "risk-low"; // 매우낮음 - 초록색
      return "risk-medium";
    },
    currentRiskClass() {
      const riskValue = this.currentRisk || 0;
      if (riskValue >= 16) return "risk-very-high"; // 매우높음 - 빨간색
      if (riskValue >= 9) return "risk-high"; // 높음/약간높음 - 주황색
      if (riskValue >= 4) return "risk-medium"; // 보통/낮음 - 노란색
      if (riskValue >= 1) return "risk-low"; // 매우낮음 - 초록색
      return "risk-medium";
    },
    improvedRiskClass() {
      const riskValue = (this.improvementData && this.improvementData.개선후_위험성) || 0;
      if (riskValue >= 16) return "risk-very-high"; // 매우높음 - 빨간색
      if (riskValue >= 9) return "risk-high"; // 높음/약간높음 - 주황색
      if (riskValue >= 4) return "risk-medium"; // 보통/낮음 - 노란색
      if (riskValue >= 1) return "risk-low"; // 매우낮음 - 초록색
      return "risk-medium";
    },
  },
  data() {
    return {
      windowWidth: window.innerWidth,
    };
  },
  mounted() {
    // 화면 크기 변경 감지
    this.handleResize = () => {
      this.windowWidth = window.innerWidth;
    };
    window.addEventListener("resize", this.handleResize);

    const backButtonRouteGuard = this.$router.beforeEach((to, from, next) => {
      if (this.showPopup) {
        this.showPopup = false;
        next(false);
        return;
      }
      next(true);
    });
    this.$once("hook:destroyed", () => {
      backButtonRouteGuard();
      window.removeEventListener("resize", this.handleResize);
    });
  },
  methods: {
    handleClose() {
      this.showPopup = false;
    },
  },
};
</script>

<style scoped lang="scss">
.improvement-container {
  padding: 20px;
}

.header-info {
  .improvement-summary {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 20px;
    padding: 20px;
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
    border-radius: 12px;
    border: 1px solid #e9ecef;

    .summary-item {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: center;
      gap: 12px;

      .summary-label {
        font-size: 16px;
        color: #666;
        font-weight: 600;
        white-space: nowrap;

        &.left {
          order: 1;
        }

        &.right {
          order: 2;
        }
      }

      .summary-value {
        font-size: 28px;
        font-weight: 700;
        padding: 12px 20px;
        border-radius: 10px;
        min-width: 100px;
        text-align: center;
        border: 2px solid;
        transition: all 0.3s ease;
        order: 2;

        &.risk-very-high {
          background: #ffebee;
          color: #c62828;
          border-color: #ef5350;
        }

        &.risk-high {
          background: #fff3e0;
          color: #ef6c00;
          border-color: #ff9800;
        }

        &.risk-medium {
          background: #fffde7;
          color: #f57f17;
          border-color: #ffeb3b;
        }

        &.risk-low {
          background: #e8f5e8;
          color: #2e7d32;
          border-color: #4caf50;
        }
      }
    }

    .summary-arrow {
      display: flex;
      align-items: center;
      justify-content: center;
      color: #007bff;
    }

    .summary-improvement {
      display: flex;
      align-items: center;
      justify-content: center;

      .improvement-badge {
        background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
        color: white;
        padding: 8px 16px;
        border-radius: 20px;
        font-weight: 600;
        font-size: 14px;
        box-shadow: 0 2px 8px rgba(40, 167, 69, 0.3);
      }
    }
  }

  .management-standard {
    display: flex;
    justify-content: center;

    .standard-box {
      display: flex;
      align-items: center;
      padding: 12px 20px;
      border-radius: 8px;
      font-weight: 600;
      border: 1px solid;
      transition: all 0.3s ease;

      &.risk-very-high {
        background: #ffebee;
        color: #c62828;
        border-color: #ef5350;
      }

      &.risk-high {
        background: #fff3e0;
        color: #ef6c00;
        border-color: #ff9800;
      }

      &.risk-medium {
        background: #fffde7;
        color: #f57f17;
        border-color: #ffeb3b;
      }

      &.risk-low {
        background: #e8f5e8;
        color: #2e7d32;
        border-color: #4caf50;
      }
    }
  }
}

.improvement-details {
  .section-title {
    display: flex;
    align-items: center;
    font-size: 18px;
    font-weight: 600;
    margin-bottom: 20px;
    color: #333;
  }

  .detail-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 12px;

    .detail-card {
      background: #fff;
      border: 1px solid #e9ecef;
      border-radius: 8px;
      padding: 12px;
      transition: all 0.2s ease;

      &:hover {
        border-color: #007bff;
        box-shadow: 0 2px 8px rgba(0, 123, 255, 0.1);
      }

      &.highlight {
        grid-column: 1 / -1;
        background: #f8f9fa;
        border-color: #007bff;
      }

      .detail-header {
        display: flex;
        align-items: center;
        margin-bottom: 12px;

        .detail-title {
          font-weight: 600;
          color: #333;
        }
      }

      .detail-content {
        line-height: 1.6;
        color: #555;
        font-size: 14px;
      }
    }
  }
}

.ai-info {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px;
  padding: 20px;
  color: white;

  .ai-badge {
    display: flex;
    align-items: center;
    font-weight: 600;
    margin-bottom: 12px;
    font-size: 16px;
  }

  .ai-description {
    line-height: 1.6;
    opacity: 0.9;
    margin: 0;
    font-size: 14px;
  }
}

.button-container {
  display: flex;
  justify-content: center;
  padding-top: 20px;
  border-top: 1px solid #e9ecef;
  margin-top: 20px;
}

// 모바일 레이아웃 스타일
.mobile-layout {
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;

  .mobile-sticky-header {
    position: sticky;
    top: 0;
    z-index: 10;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 12px 16px;
    background: #f8f9fa;
    border-radius: 8px;
    margin-bottom: 12px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

    .header-title {
      display: flex;
      align-items: center;
      font-size: 16px;
      font-weight: 600;
      color: #333;
    }
  }

  .mobile-content-wrapper {
    flex: 1;
    overflow-y: auto;
    padding-bottom: 10px;
  }

  .mobile-improvement-summary {
    background: white;
    border: 1px solid #e9ecef;
    border-radius: 12px;
    padding: 16px;
    margin-bottom: 16px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

    .mobile-risk-comparison {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 16px;

      .mobile-risk-item {
        display: flex;
        flex-direction: column;
        align-items: center;
        text-align: center;
        width: 100%;

        .risk-label {
          font-size: 14px;
          color: #666;
          margin-bottom: 8px;
          font-weight: 600;
        }

        .risk-value {
          font-size: 24px;
          font-weight: 700;
          padding: 12px 20px;
          border-radius: 12px;
          width: 120px;
          text-align: center;
          border: 2px solid;
          transition: all 0.3s ease;

          &.risk-very-high {
            background: #ffebee;
            color: #c62828;
            border-color: #ef5350;
          }

          &.risk-high {
            background: #fff3e0;
            color: #ef6c00;
            border-color: #ff9800;
          }

          &.risk-medium {
            background: #fffde7;
            color: #f57f17;
            border-color: #ffeb3b;
          }

          &.risk-low {
            background: #e8f5e8;
            color: #2e7d32;
            border-color: #4caf50;
          }
        }
      }

      .mobile-arrow-down {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 8px;
        color: #007bff;

        .improvement-badge-mobile {
          background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
          color: white;
          padding: 6px 12px;
          border-radius: 16px;
          font-weight: 600;
          font-size: 12px;
          box-shadow: 0 2px 6px rgba(40, 167, 69, 0.3);
        }
      }
    }
  }

  .mobile-details-section {
    margin-bottom: 16px;

    .mobile-section-title {
      display: flex;
      align-items: center;
      font-size: 16px;
      font-weight: 600;
      margin-bottom: 12px;
      color: #333;
      padding: 0 4px;
    }

    .mobile-detail-cards {
      .mobile-detail-card {
        background: white;
        border: 1px solid #e9ecef;
        border-radius: 8px;
        padding: 12px;
        margin-bottom: 8px;
        transition: all 0.2s ease;

        &:hover {
          border-color: #007bff;
          box-shadow: 0 2px 6px rgba(0, 123, 255, 0.1);
        }

        &.highlight {
          background: #f8f9fa;
          border-color: #007bff;
          border-width: 2px;
        }

        .mobile-detail-header {
          display: flex;
          align-items: center;
          margin-bottom: 8px;

          .mobile-detail-title {
            font-weight: 600;
            color: #333;
            font-size: 14px;
          }
        }

        .mobile-detail-content {
          line-height: 1.5;
          color: #555;
          font-size: 13px;
          word-break: break-word;
        }
      }
    }
  }

  .mobile-ai-info {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 12px;
    padding: 16px;
    color: white;
    margin-bottom: 16px;

    .mobile-ai-badge {
      display: flex;
      align-items: center;
      font-weight: 600;
      margin-bottom: 10px;
      font-size: 14px;
    }

    .mobile-ai-description {
      line-height: 1.5;
      opacity: 0.9;
      margin: 0;
      font-size: 13px;
    }
  }

  .mobile-bottom-spacer {
    height: 80px;
  }
}

.mobile-button-container {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #e9ecef;

  &.mobile-sticky-buttons {
    position: sticky;
    bottom: 0;
    z-index: 10;
    background: #f8f9fa;
    margin-top: 0;
    padding: 12px;
    border-top: 1px solid #e9ecef;
    box-shadow: 0 -2px 4px rgba(0, 0, 0, 0.1);
    display: flex;
    justify-content: center;
  }

  .mobile-button {
    min-height: 44px;
    font-size: 15px;
    font-weight: 600;
    border-radius: 8px;
    padding: 12px 24px;
    min-width: 140px;
    transition: all 0.3s ease;

    &.mobile-button-primary {
      background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
      color: white;
      border: none;
      box-shadow: 0 2px 6px rgba(0, 123, 255, 0.3);

      &:hover {
        box-shadow: 0 4px 12px rgba(0, 123, 255, 0.4);
        transform: translateY(-1px);
      }
    }
  }
}

// 모바일 환경에서 팝업 스타일 조정
@media (max-width: 767px) {
  #popupImprovementEffect::v-deep {
    .vs-popup {
      .vs-popup--content {
        margin: 10px;
        max-height: 85vh;
        overflow-y: auto;
        padding: 10px !important;
      }
      .vs-popup--content {
        width: calc(100% - 20px);
      }

      .vs-popup--header {
        padding: 16px 20px 12px;

        .vs-popup--title {
          font-size: 18px;
          font-weight: 600;
        }
      }
    }
  }

  .mobile-layout {
    padding: 0;
  }

  .mobile-button-container {
    margin-left: -10px;
    margin-right: -10px;
    margin-bottom: -10px;
    padding: 10px;
    background: #f8f9fa;
    border-top: 1px solid #e9ecef;

    &.mobile-sticky-buttons {
      margin-left: -10px;
      margin-right: -10px;
      margin-bottom: -10px;
      padding: 12px;
      position: sticky;
      bottom: -10px;
      z-index: 10;
      background: #f8f9fa;
      border-top: 1px solid #e9ecef;
      box-shadow: 0 -2px 4px rgba(0, 0, 0, 0.1);
    }
  }
}

// PC 환경에서 기존 스타일 유지
@media (min-width: 768px) {
  #popupImprovementEffect::v-deep {
    .vs-popup {
      min-width: 800px;
      .vs-popup--content {
        max-height: 90vh;
        overflow-y: auto;
      }
    }
  }
}
</style>
